interface PricePoint {
  price: number;
  recordedAt: string;
}

interface MerchantPriceData {
  merchantName: string;
  affiliateNetwork?: string | null;
  affiliateUrl?: string | null;
  currentPrice: number | null;
  originalPrice: number | null;
  inStock?: boolean;
  history: PricePoint[];
}

export interface PriceHistoryData {
  ean: string;
  days: number;
  lowestPrice: number;
  highestPrice: number;
  merchantCount: number;
  merchants: MerchantPriceData[];
}

export const usePriceHistory = (ean: Ref<string | undefined> | string) => {
  const data = ref<PriceHistoryData | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const fetchHistory = async (days = 90) => {
    const eanValue = unref(ean);
    if (!eanValue) return;

    loading.value = true;
    error.value = null;
    try {
      const api = useApi();
      const response = await api<any>(`/api/price-history/${eanValue}`, {
        query: { days },
      });
      data.value = response;
    } catch (err: any) {
      error.value =
        err.data?.statusMessage || "Preisdaten konnten nicht geladen werden";
      data.value = null;
    } finally {
      loading.value = false;
    }
  };

  // Price trend: compare current lowest to oldest recorded price
  const priceTrend = computed(() => {
    if (!data.value || data.value.merchants.length === 0) return null;
    const merchant = data.value.merchants[0];
    if (!merchant || merchant.history.length < 2) return null;
    const oldestEntry = merchant.history[0];
    const newestEntry = merchant.history[merchant.history.length - 1];
    if (!oldestEntry || !newestEntry) return null;
    const oldest = oldestEntry.price;
    const newest = newestEntry.price;
    const diff = newest - oldest;
    const percent = Math.round((diff / oldest) * 100);
    return {
      direction:
        diff > 0
          ? "up"
          : diff < 0
            ? "down"
            : ("stable" as "up" | "down" | "stable"),
      percent: Math.abs(percent),
      diff,
    };
  });

  return { data, loading, error, fetchHistory, priceTrend };
};
